/**
 * Date utilities.
 *
 * IMPORTANT: Never use `new Date().toISOString().split('T')[0]` for a user-facing
 * "today" default. In US local evening, UTC is already tomorrow — users see
 * form fields pre-filled with the wrong date.
 *
 * Use todayLocal() for HTML `<input type="date">` defaults and any other
 * user-facing YYYY-MM-DD value that should match the user's wall clock.
 */

/** Today's date in the user's local timezone, formatted YYYY-MM-DD. */
export function todayLocal(): string {
  // 'en-CA' locale formats as YYYY-MM-DD (ISO-8601-ish) without TZ conversion.
  return new Date().toLocaleDateString('en-CA');
}

/** Convert any Date to local YYYY-MM-DD. */
export function toLocalDateString(d: Date): string {
  return d.toLocaleDateString('en-CA');
}

/**
 * Calendar V1: convert a YYYY-MM-DD string into a JS Date at local midnight.
 * Use this when a UI library (e.g. react-day-picker) requires a Date object
 * representing a calendar day — `new Date('2026-05-20')` would interpret the
 * input as UTC midnight which displays as the prior day in negative-UTC TZs.
 *
 * @example
 *   isoToLocalDate('2026-05-20') // → Wed May 20 2026 00:00:00 local
 */
export function isoToLocalDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

/**
 * Calendar V1: convert a local-midnight Date back to YYYY-MM-DD. Inverse of
 * isoToLocalDate. Equivalent to toLocalDateString but takes the explicit
 * naming pair so it's grep-able with isoToLocalDate.
 *
 * @example
 *   localDateToIso(new Date(2026, 4, 20)) // → '2026-05-20'
 */
export function localDateToIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Whole calendar days between two ISO dates (YYYY-MM-DD), counted in LOCAL
 * calendar days. Positive = `toIso` is after `fromIso`. Both operands are
 * local midnight, so the subtraction is a clean integer day count regardless
 * of timezone or time of day. Math.round (not ceil) so DST transitions (a
 * 23h or 25h "day") don't tip the result by one.
 */
export function daysBetweenIso(fromIso: string, toIso: string): number {
  const from = isoToLocalDate(fromIso.slice(0, 10));
  const to = isoToLocalDate(toIso.slice(0, 10));
  return Math.round((to.getTime() - from.getTime()) / 86_400_000);
}

/**
 * Whole calendar days from local-today to an ISO date (YYYY-MM-DD).
 * Positive = future, negative = past, 0 = today. TZ-safe.
 *
 * W2-25 fix (2026-06-11): replaces the `Math.ceil((new Date(iso) - Date.now())
 * / 86.4M)` pattern, which compared UTC-midnight-due against the live UTC
 * instant — in US evening, UTC is already tomorrow, so upcoming counts ran
 * short by 1 and overdue counts ran long by 1.
 */
export function daysUntilLocal(iso: string): number {
  return daysBetweenIso(todayLocal(), iso);
}

/**
 * Calendar V2 (2026-05-21): enumerate every weekday-matching date in a
 * given month. Used by the weekday-shortcut buttons in /hours form (e.g.
 * "All Mondays in this month", "All weekdays", "All weekends").
 *
 * @param year     full year (e.g. 2026)
 * @param month    1-indexed month (1 = January, 12 = December)
 * @param kind     'monday' | 'weekday' | 'weekend' | dayOfWeek 0-6
 *                 (0=Sunday, 1=Monday, ..., 6=Saturday)
 * @returns        sorted ISO date strings (YYYY-MM-DD) for every day in
 *                 the month matching the criteria
 *
 * @example
 *   getDatesInMonth(2026, 5, 'monday')  // every Monday in May 2026
 *   getDatesInMonth(2026, 5, 'weekday') // Mon-Fri in May 2026
 *   getDatesInMonth(2026, 5, 'weekend') // Sat+Sun in May 2026
 *   getDatesInMonth(2026, 5, 3)         // every Wednesday in May 2026
 */
export function getDatesInMonth(
  year: number,
  month: number,
  kind: 'monday' | 'weekday' | 'weekend' | number,
): string[] {
  const out: string[] = [];
  // Day 0 of next month = last day of current month (JS trick).
  const lastDay = new Date(year, month, 0).getDate();
  for (let day = 1; day <= lastDay; day++) {
    const d = new Date(year, month - 1, day);
    const dow = d.getDay(); // 0=Sun .. 6=Sat
    let match = false;
    if (kind === 'monday') match = dow === 1;
    else if (kind === 'weekday') match = dow >= 1 && dow <= 5;
    else if (kind === 'weekend') match = dow === 0 || dow === 6;
    else if (typeof kind === 'number') match = dow === kind;
    if (match) out.push(localDateToIso(d));
  }
  return out;
}

/**
 * Calendar V2b (2026-05-21): return all 11 US federal holiday dates for a
 * given calendar year as YYYY-MM-DD ISO strings. Used by the DayPicker on
 * /hours to surface federal holidays as visual markers — users see them
 * at a glance when planning shifts/billable days, and can still click to
 * select (holiday classification is informational, not a selection block).
 *
 * Observed-day handling: federal holidays falling on Saturday are observed
 * the prior Friday; falling on Sunday are observed the following Monday.
 * The MARKER follows the observed date, not the nominal date, because that's
 * what affects worker schedules (banks closed, federal offices closed).
 *
 * The 11 federal holidays per 5 U.S.C. § 6103:
 *   - New Year's Day (Jan 1, observed)
 *   - Martin Luther King Jr. Day (3rd Monday of January)
 *   - Presidents' Day / Washington's Birthday (3rd Monday of February)
 *   - Memorial Day (last Monday of May)
 *   - Juneteenth National Independence Day (Jun 19, observed) — added 2021
 *   - Independence Day (Jul 4, observed)
 *   - Labor Day (1st Monday of September)
 *   - Columbus Day (2nd Monday of October)
 *   - Veterans Day (Nov 11, observed)
 *   - Thanksgiving Day (4th Thursday of November)
 *   - Christmas Day (Dec 25, observed)
 *
 * @param year  full year (e.g. 2026)
 * @returns     sorted ISO date strings for the 11 observed holiday dates
 *
 * Edge case (Jan 1 falls on Saturday — years 2022, 2028, 2033, 2039, …):
 *   When Jan 1 = Sat, the observation rule shifts the observed date to
 *   Dec 31 of the PRIOR year. The returned array therefore contains
 *   one date in `year - 1` (`{year-1}-12-31`) instead of `{year}-01-01`.
 *   Callers needing strict "dates in calendar year Y" semantics should
 *   filter `out.filter(d => d.startsWith(String(year)))` after this call.
 *   The Hours.tsx DayPicker consumer is unaffected because it merges
 *   prev/curr/next year ranges, so the Dec 31 leak still appears at the
 *   correct visual position regardless of which year arg produced it.
 *
 * @example
 *   getUSFederalHolidays(2026)
 *   // → ['2026-01-01', '2026-01-19', '2026-02-16', '2026-05-25',
 *   //    '2026-06-19', '2026-07-03', '2026-09-07', '2026-10-12',
 *   //    '2026-11-11', '2026-11-26', '2026-12-25']
 *
 * @example
 *   getUSFederalHolidays(2022)
 *   // → ['2021-12-31', '2022-01-17', '2022-02-21', ...]
 *   //    └─ Jan 1 2022 = Sat, observed Fri Dec 31 2021 (prior year date)
 */
export function getUSFederalHolidays(year: number): string[] {
  // Apply weekend-observation rule: Sat → prior Fri, Sun → following Mon.
  const observed = (d: Date): Date => {
    const dow = d.getDay();
    if (dow === 6) return new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1); // Sat → Fri
    if (dow === 0) return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1); // Sun → Mon
    return d;
  };
  // Find the Nth occurrence of a weekday in a given month.
  // weekday: 0=Sun..6=Sat. n: 1-based ordinal.
  const nthWeekday = (y: number, monthIdx: number, weekday: number, n: number): Date => {
    const d = new Date(y, monthIdx, 1);
    const offset = (weekday - d.getDay() + 7) % 7;
    return new Date(y, monthIdx, 1 + offset + (n - 1) * 7);
  };
  // Last weekday of a month (used for Memorial Day = last Monday of May).
  const lastWeekday = (y: number, monthIdx: number, weekday: number): Date => {
    const lastDay = new Date(y, monthIdx + 1, 0); // day 0 of next month = last of this
    const offset = (lastDay.getDay() - weekday + 7) % 7;
    return new Date(y, monthIdx, lastDay.getDate() - offset);
  };

  const dates: Date[] = [
    observed(new Date(year, 0, 1)),         // New Year's Day
    nthWeekday(year, 0, 1, 3),              // MLK Day = 3rd Mon Jan
    nthWeekday(year, 1, 1, 3),              // Presidents' Day = 3rd Mon Feb
    lastWeekday(year, 4, 1),                // Memorial Day = last Mon May
    observed(new Date(year, 5, 19)),        // Juneteenth
    observed(new Date(year, 6, 4)),         // Independence Day
    nthWeekday(year, 8, 1, 1),              // Labor Day = 1st Mon Sep
    nthWeekday(year, 9, 1, 2),              // Columbus Day = 2nd Mon Oct
    observed(new Date(year, 10, 11)),       // Veterans Day
    nthWeekday(year, 10, 4, 4),             // Thanksgiving = 4th Thu Nov
    observed(new Date(year, 11, 25)),       // Christmas Day
  ];
  return dates.map(localDateToIso).sort();
}

/**
 * Format a Postgres DATE value (or ISO-date-at-midnight-UTC) for user display
 * without timezone drift. Postgres serializes DATE columns as either
 * "2022-02-15" or "2022-02-15T00:00:00.000Z" depending on the driver path.
 *
 * Using `new Date(s).toLocaleDateString()` on either form treats midnight-UTC
 * as a timestamp, so a user in PT (UTC-8) sees "2022-02-14" — one day earlier
 * than the actual calendar date.
 *
 * This helper extracts the YYYY-MM-DD portion explicitly and formats it as
 * a local calendar date without ever constructing a timestamp.
 *
 * @param value    YYYY-MM-DD string, ISO datetime string, Date, or null/undefined
 * @param fallback what to render when value is missing (default: em dash)
 * @param style    "short" (M/D/YYYY) or "medium" (Mon D, YYYY) — default "medium"
 */
export function formatISODate(
  value: string | Date | null | undefined,
  fallback: string = '\u2014',
  style: 'short' | 'medium' = 'medium',
): string {
  if (value == null || value === '') return fallback;
  let y: number, m: number, d: number;
  if (value instanceof Date) {
    y = value.getFullYear(); m = value.getMonth() + 1; d = value.getDate();
  } else {
    // Match leading YYYY-MM-DD regardless of what follows (T..., space+time, nothing).
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (!match) return String(value);
    y = parseInt(match[1], 10);
    m = parseInt(match[2], 10);
    d = parseInt(match[3], 10);
  }
  if (style === 'short') return `${m}/${d}/${y}`;
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${MONTHS[m - 1]} ${d}, ${y}`;
}
